import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../contexts/AuthContext';

const schema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

type FormData = z.infer<typeof schema>;

export default function ForgotPassword() {
  const { resetPassword } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: FormData) => {
    if (isLoading) return;
    setIsLoading(true);
    setError(null);

    const { error: resetError } = await resetPassword(data.email);

    // Always show success state regardless of whether the email exists —
    // do not reveal account presence to unauthenticated callers.
    if (resetError) {
      console.error('[ForgotPassword]', resetError);
      // Only surface rate-limit / network errors to the user; otherwise
      // fall through to the generic "check your inbox" message.
      if (resetError.message.toLowerCase().includes('rate limit')) {
        setError('Too many attempts. Please wait a few minutes and try again.');
        setIsLoading(false);
        return;
      }
    }

    setSent(true);
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex items-start justify-start pt-[8vh] pb-6 px-4 sm:px-6 lg:pl-[8%] auth-bg">
      <div className="fixed inset-0 bg-navy/70" aria-hidden="true" />
      <div className="relative max-w-md w-full space-y-4">
        <div className="w-full">
          <div className="mb-4 flex justify-center">
            <img
              src="/images/wingmentor-logo.png"
              alt="WingMentor Logo"
              className="h-20 w-auto opacity-70"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </div>
          <h2 className="mb-1 text-center text-xl font-heading font-semibold text-white">
            Reset your password
          </h2>
          <p className="text-center text-sm text-white/80">
            Enter your email and we'll send a reset link.
          </p>
        </div>

        <div className="space-y-4 rounded-xl border-2 border-orca-elevate/15 bg-white/50 px-7 py-5 shadow-2xl backdrop-blur-sm backdrop-saturate-125 ring-1 ring-white/35">
          {sent ? (
            <div className="space-y-4">
              <div className="bg-green-50 border-l-4 border-green-500 text-navy px-4 py-3 rounded">
                <p className="font-medium text-sm">Check your inbox</p>
                <p className="text-sm mt-1">
                  If an account exists for that email, a password-reset link has been sent.
                  The link expires after a short period; request another if needed.
                </p>
              </div>
              <Link
                to="/login"
                className="block w-full text-center font-medium text-orca-elevate hover:text-orca-elevate/80"
              >
                Back to sign in
              </Link>
            </div>
          ) : (
            <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
              {error && (
                <div className="bg-signal/10 border-l-4 border-signal text-navy px-4 py-2.5 rounded">
                  <p className="font-medium text-sm">Error</p>
                  <p className="text-sm">{error}</p>
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-navy mb-1">
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  {...register('email')}
                  className="appearance-none relative block w-full px-3 py-2 bg-white border border-steel/30 rounded-md placeholder-steel/50 text-navy focus:outline-none focus:ring-2 focus:ring-orca-elevate focus:border-orca-elevate sm:text-sm"
                  placeholder="you@example.com"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Sending…' : 'Send reset link'}
              </button>

              <div className="text-sm text-center">
                <Link
                  to="/login"
                  className="font-medium text-orca-elevate hover:text-orca-elevate/80"
                >
                  Back to sign in
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
